import React from 'react';
import AdminLayout from '../../components/AdminLayout';
import { fetchWhatsAppMessages, WhatsAppMessageDto } from '@features/whatsapp/api';

const PAGE_SIZE = 20;

const WhatsAppMessagesPage: React.FC = () => {
  const [messages, setMessages] = React.useState<WhatsAppMessageDto[]>([]);
  const [page, setPage] = React.useState(0);
  const [totalPages, setTotalPages] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchWhatsAppMessages(page, PAGE_SIZE)
      .then(res => {
        if (cancelled) return;
        setMessages(res.content);
        setTotalPages(res.totalPages);
      })
      .catch(err => {
        if (cancelled) return;
        setError(err?.message ?? 'Chyba při načítání zpráv');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [page]);

  const formatDate = (iso: string | null) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return d.toLocaleString('cs-CZ');
  };

  const truncate = (text: string | null, max = 80) => {
    if (!text) return '-';
    return text.length > max ? `${text.slice(0, max)}…` : text;
  };

  const intentLabel: Record<string, string> = {
    BOOKING_REQUEST: 'Rezervace',
    INFO_QUESTION: 'Dotaz',
    CANCELLATION_REQUEST: 'Zrušení',
    RESCHEDULE_REQUEST: 'Přesun',
    OTHER: 'Jiné',
  };

  const intentColor: Record<string, string> = {
    BOOKING_REQUEST: 'bg-purple-100 text-purple-800',
    INFO_QUESTION: 'bg-cyan-100 text-cyan-800',
    CANCELLATION_REQUEST: 'bg-red-100 text-red-800',
    RESCHEDULE_REQUEST: 'bg-amber-100 text-amber-800',
    OTHER: 'bg-gray-100 text-gray-700',
  };

  return (
    <AdminLayout title="WhatsApp zprávy">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Příchozí a odchozí WhatsApp zprávy</h2>
          <p className="text-sm text-gray-500 mt-1">Seřazeno od nejnovějších</p>
        </div>

        {error && (
          <div className="p-4 bg-red-50 border-b border-red-200 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Čas</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Směr</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Telefon</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Zpráva</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">AI</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Zákazník</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading && (
                <tr>
                  <td colSpan={7} className="px-4 py-6 text-center text-sm text-gray-500">Načítám…</td>
                </tr>
              )}
              {!loading && messages.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-6 text-center text-sm text-gray-500">Žádné zprávy</td>
                </tr>
              )}
              {!loading && messages.map(msg => (
                <tr key={msg.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{formatDate(msg.receivedAt)}</td>
                  <td className="px-4 py-3 text-sm whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      msg.direction === 'INBOUND' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                    }`}>
                      {msg.direction === 'INBOUND' ? 'Příchozí' : 'Odchozí'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                    {msg.direction === 'INBOUND' ? msg.fromPhone : msg.toPhone}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 max-w-md">{truncate(msg.body)}</td>
                  <td className="px-4 py-3 text-sm max-w-sm">
                    {msg.aiError ? (
                      <span className="text-xs text-red-600" title={msg.aiError}>chyba</span>
                    ) : msg.aiIntent ? (
                      <div className="flex flex-col gap-1" title={msg.aiSummary ?? ''}>
                        <span className={`inline-flex w-fit items-center px-2 py-0.5 rounded text-xs font-medium ${intentColor[msg.aiIntent] ?? 'bg-gray-100 text-gray-700'}`}>
                          {intentLabel[msg.aiIntent] ?? msg.aiIntent}
                          {msg.aiConfidence != null && (
                            <span className="ml-1 opacity-70">{Math.round(msg.aiConfidence * 100)}%</span>
                          )}
                        </span>
                        {msg.aiSummary && (
                          <span className="text-xs text-gray-600 truncate">{truncate(msg.aiSummary, 80)}</span>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-300">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                    {msg.customerName ?? <span className="text-gray-400 italic">neznámý</span>}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{msg.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="px-6 py-4 flex items-center justify-between border-t border-gray-200">
            <button
              className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50"
              disabled={page === 0 || loading}
              onClick={() => setPage(p => Math.max(0, p - 1))}
            >
              Předchozí
            </button>
            <span className="text-sm text-gray-700">
              Strana {page + 1} z {totalPages}
            </span>
            <button
              className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50"
              disabled={page + 1 >= totalPages || loading}
              onClick={() => setPage(p => p + 1)}
            >
              Další
            </button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default WhatsAppMessagesPage;
